// #region Video Quality Level

import { WAVEFORM_ELEMENTS } from "@/utils/constants";

const VIDEO_QUALITY_KEY = 'glim_video_quality';

export function restoreVideoQualityLevel() {
  return localStorage.getItem(VIDEO_QUALITY_KEY) || '';
}

export function storeVideoQualityLevel(level) {
  localStorage.setItem(VIDEO_QUALITY_KEY, level);
}

// #endregion Video Quality Level

// #region Is Muted

const IS_MUTED_KEY = 'glim_is_muted';

export function restoreIsMuted() {
  return JSON.parse(localStorage.getItem(IS_MUTED_KEY)) || false;
}

export function storeIsMuted(isMuted) {
  localStorage.setItem(IS_MUTED_KEY, JSON.stringify(isMuted));
}

// #endregion Is Muted

// #region Volume

const VOLUME_KEY = 'glim_volume';

export function restoreVolume() {
  return JSON.parse(localStorage.getItem(VOLUME_KEY)) || 1;
}

export function storeVolume(volume) {
  localStorage.setItem(VOLUME_KEY, JSON.stringify(volume));
}

// #endregion Volume

// #region Overlays

const OVERLAYS_KEY = 'glim_overlays';

export function restoreOverlays() {
  return JSON.parse(localStorage.getItem(OVERLAYS_KEY)) || [];
}

export function storeOverlays(overlays) {
  localStorage.setItem(OVERLAYS_KEY, JSON.stringify(overlays));
}

// #endregion Overlays

// #region Captions

const SUBTITLE_SOURCE_KEY = 'glim_subtitle_source';

export function restoreSubtitleSource() {
  let item = localStorage.getItem(SUBTITLE_SOURCE_KEY);
  if (!item || item === 'undefined'){
    return 'Off'
  }
  return JSON.parse(item);
}

export function storeSubtitleSource(source) {
  localStorage.setItem(SUBTITLE_SOURCE_KEY, JSON.stringify(source));
}

// #endregion Captions

// #region Waveform Marks

const SELECTED_WAVEFORM_MARKS = 'glim_selected_waveform_marks';
const SELECTED_WAVEFORM_DEFAULT = [
  WAVEFORM_ELEMENTS.WAVEFORM, 
  WAVEFORM_ELEMENTS.GOP, 
  WAVEFORM_ELEMENTS.CLIPS
];

export function restoreSelectedWaveformMarks() {
  return JSON.parse(localStorage.getItem(SELECTED_WAVEFORM_MARKS)) || SELECTED_WAVEFORM_DEFAULT;
}

export function storeSelectedWaveformMarks(selectedWaveformMarks) {
  localStorage.setItem(SELECTED_WAVEFORM_MARKS, JSON.stringify(selectedWaveformMarks));
}

// #endregion Waveform Marks

// #region Simulate Alpha

const SIMULATE_ALPHA_KEY = 'glim_simulate_alpha';

export function restoreSimulateAlpha() {
  return JSON.parse(localStorage.getItem(SIMULATE_ALPHA_KEY)) || false;
}

export function storeSimulateAlpha(isSimulated) {
  localStorage.setItem(SIMULATE_ALPHA_KEY, JSON.stringify(isSimulated));
}

// #endregion Simulate Alpha

// #region Clip List

const CLIP_LIST_KEY = 'glim_clip_list';

export function restoreClipList() {
  return JSON.parse(localStorage.getItem(CLIP_LIST_KEY)) || [];
}

export function storeClipList(cliplist) {
  localStorage.setItem(CLIP_LIST_KEY, JSON.stringify(cliplist));
}

const CLIP_LIST_OUTPUTS_KEY = 'glim_clip_list_outputs';

export function restoreClipListOutputConfiguration(name) {
  const configurations = JSON.parse(localStorage.getItem(CLIP_LIST_OUTPUTS_KEY)) || {};
  return configurations[name];
}

export function storeClipListOutputConfiguration(name, configuration) {
  const configurations = JSON.parse(localStorage.getItem(CLIP_LIST_OUTPUTS_KEY)) || {}
  configurations[name] = configuration;

  localStorage.setItem(CLIP_LIST_OUTPUTS_KEY, JSON.stringify(configurations));
}

export function removeObsoleteCliplistOutputConfigurations(outputNames) {
  const configurations = JSON.parse(localStorage.getItem(CLIP_LIST_OUTPUTS_KEY)) || {};
  const obsoletOutputs = Object.keys(configurations).filter(k => !outputNames.includes(k));

  obsoletOutputs.forEach(o => delete configurations[o]);

  localStorage.setItem(CLIP_LIST_OUTPUTS_KEY, JSON.stringify(configurations));
}

const CLIP_LIST_OUTPUT_SELECTION_KEY = 'glim_clip_list_output_selection';

export function restoreClipListOutputSelection() {
  return localStorage.getItem(CLIP_LIST_OUTPUT_SELECTION_KEY) || '';
}

export function storeClipListOutputSelection(output) {
  localStorage.setItem(CLIP_LIST_OUTPUT_SELECTION_KEY, output);
}

// #endregion Clip List

// #region Split

const COLUMNS_SPLIT_KEY = 'glim_columns_split';
export const COLUMNS_SPLIT_DEFAULT = screen.width <= 1920 ? 53 : 70;

export function restoreColumnsSplit() {
  return JSON.parse(localStorage.getItem(COLUMNS_SPLIT_KEY)) || COLUMNS_SPLIT_DEFAULT;
}

export function storeColumnsSplit(percentage) {
  localStorage.setItem(COLUMNS_SPLIT_KEY, percentage);
}

const MEDIA_COLUMN_SPLIT_KEY = 'glim_media_column_split';
export const MEDIA_COLUMN_SPLIT_DEFAULT = 78;

export function restoreMediaColumnSplit() {
  return JSON.parse(localStorage.getItem(MEDIA_COLUMN_SPLIT_KEY)) || MEDIA_COLUMN_SPLIT_DEFAULT;
}

export function storeMediaColumnSplit(percentage) {
  localStorage.setItem(MEDIA_COLUMN_SPLIT_KEY, JSON.stringify(percentage));
}

const INFO_COLUMN_SPLIT_KEY = 'glim_info_column_split';
export const INFO_COLUMN_SPLIT_DEFAULT = 50;

export function restoreInfoColumnSplit() {
  return JSON.parse(localStorage.getItem(INFO_COLUMN_SPLIT_KEY)) || INFO_COLUMN_SPLIT_DEFAULT;
}

export function storeInfoColumnSplit(percentage) {
  localStorage.setItem(INFO_COLUMN_SPLIT_KEY, JSON.stringify(percentage));
}

// #endregion Split

// #region Tabs

const TOP_TABS_KEY = 'glim_top_tabs';
export const TOP_TABS_DEFAULT = [
  'Audio',
  'Frame',
  'Clips',
  'Scopes'
];

export function restoreTopTabs() {
  return JSON.parse(localStorage.getItem(TOP_TABS_KEY)) || TOP_TABS_DEFAULT;
}

export function storeTopTabs(tabs) {
  localStorage.setItem(TOP_TABS_KEY, JSON.stringify(tabs));
}

const BOTTOM_TABS_KEY = 'glim_bottom_tabs';
export const BOTTOM_TABS_DEFAULT = [
  'Inspector',
  'Plugins'
];

export function restoreBottomTabs() {
  return JSON.parse(localStorage.getItem(BOTTOM_TABS_KEY)) || BOTTOM_TABS_DEFAULT;
}

export function storeBottomTabs(tabs) {
  localStorage.setItem(BOTTOM_TABS_KEY, JSON.stringify(tabs));
}

// #endregion Tabs

// #region File Browser

const FILE_BROWSER_FAVORITES = 'glim_file_browser_favorites';

export function restoreFileBrowserFavorites() {
  return JSON.parse(localStorage.getItem(FILE_BROWSER_FAVORITES)) || [];
}

export function storefileBrowserFavorites(favorites) {
  localStorage.setItem(FILE_BROWSER_FAVORITES, JSON.stringify(favorites));
}

const FILE_BROWSER_OPEN_MODE_EXTENSIONS = 'glim_file_browser_open_mode_extensions';

export function restoreFileBrowserOpenModeExtensions() {
  return JSON.parse(localStorage.getItem(FILE_BROWSER_OPEN_MODE_EXTENSIONS)) || ['.tifo', '.mxf'];
}


const SELECTED_TIME_DISPLAY_SOURCE = 'glim_selected_time_display_source';
export function storeSelectedTimeDisplaySource(source){
  localStorage.setItem(SELECTED_TIME_DISPLAY_SOURCE, source);
}

export function restoreSelectedTimeDisplaySource(){
  return localStorage.getItem(SELECTED_TIME_DISPLAY_SOURCE);
}

// #endregion File Browser
